export interface ParsedTransaction {
  date: string;          // YYYY-MM-DD
  description: string;
  direction: 'in' | 'out';
  amount_cents: number;  // always positive
  balance_cents?: number;
  external_ref?: string; // banco/referência original para deduplicação
  raw: string;
}

export interface ParseResult {
  transactions: ParsedTransaction[];
  errors: string[];
  bank?: string;
  totalCredit: number;
  totalDebit: number;
  headers: string[];
}

// ─── Número em formato BR ──────────────────────────────────────────────────────
// Aceita: "1.234,56", "-250,00", "5,00", "1234.56", "319,33"

function parseBRCents(raw: string | undefined): number | null {
  if (!raw) return null;
  const s = raw.trim().replace(/^﻿/, '').replace(/R\$\s*/g, '').trim();
  if (!s || s === '-') return null;

  const negative = s.startsWith('-') || (s.startsWith('(') && s.endsWith(')'));
  const abs = s.replace(/^-/, '').replace(/^\(/, '').replace(/\)$/, '').trim();
  if (!abs) return null;

  let normalized: string;
  if (abs.includes(',')) {
    // Formato BR: "1.234,56" → "1234.56"
    normalized = abs.replace(/\./g, '').replace(',', '.');
  } else {
    const parts = abs.split('.');
    if (parts.length === 2 && (parts[1]?.length ?? 0) <= 2) {
      normalized = abs; // Decimal ponto: "1234.56"
    } else {
      normalized = abs.replace(/\./g, ''); // Só milhar: "1.234"
    }
  }

  const n = parseFloat(normalized);
  if (isNaN(n)) return null;
  const cents = Math.round(n * 100);
  return negative ? -cents : cents;
}

// ─── Data ────────────────────────────────────────────────────────────────────
// Aceita: DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD

function parseDate(raw: string | undefined): string | null {
  if (!raw) return null;
  const s = raw.trim();

  // DD/MM/YYYY ou DD-MM-YYYY (ambos os separadores)
  const brMatch = s.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
  if (brMatch) return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;

  // YYYY-MM-DD (ISO)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  return null;
}

// ─── Detecção de colunas ──────────────────────────────────────────────────────

function normalize(h: string): string {
  return h
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/_/g, ' ')            // underscore vira espaço antes de remover especiais
    .replace(/[^a-z0-9 ]/g, '')
    .trim();
}

// Palavras-chave de cada tipo de coluna.
// Cada entrada é uma string; se o header normalizado CONTIVER a string, é match.
const DATE_COLS = [
  'release date', 'release_date',
  'data movimento', 'data lancamento', 'data operacao',
  'data', 'date',
];

const DESC_COLS = [
  'transaction type', 'transaction_type',
  'tipo de lancamento', 'tipo lancamento', 'tipo transacao',
  'historico', 'descricao', 'description',
  'memo', 'detalhes', 'lancamento', 'discriminacao', 'title',
  'complemento', 'tipo',
];

const CREDIT_COLS = [
  'credito', 'credit', 'entrada', 'deposito', 'receita',
];

const DEBIT_COLS = [
  'debito', 'debit', 'saida', 'saque', 'despesa',
];

const AMOUNT_COLS = [
  'transaction net amount', 'transaction_net_amount',
  'net amount', 'valor liquido',
  'valor', 'value', 'amount', 'importancia', 'movimentacao',
];

const BALANCE_COLS = [
  'partial balance', 'partial_balance',
  'saldo parcial', 'saldo', 'balance',
];

const REF_COLS = [
  'reference id', 'reference_id',
  'referencia', 'reference', 'ref id',
  'docto', 'documento', 'numero do documento',
  'id transacao',
];

function detectCol(headers: string[], keywords: string[]): number {
  // Tenta match exato primeiro (keywords mais específicas primeiro)
  for (const keyword of keywords) {
    const idx = headers.findIndex((h) => normalize(h) === keyword);
    if (idx !== -1) return idx;
  }
  // Fallback: contém a keyword
  for (const keyword of keywords) {
    const idx = headers.findIndex((h) => normalize(h).includes(keyword));
    if (idx !== -1) return idx;
  }
  return -1;
}

// ─── Detecção de banco ────────────────────────────────────────────────────────

function detectBank(headers: string[], sep: string): string | undefined {
  const s = headers.join(sep).toLowerCase();
  if (s.includes('release_date') || s.includes('release date') || s.includes('transaction_net_amount'))
    return 'Mercado Pago';
  if (s.includes('docto')) return 'Itaú';
  if (s.includes('title') && s.includes('date')) return 'Nubank';
  if (s.includes('numero do documento') || s.includes('número do documento')) return 'Bradesco';
  return undefined;
}

// ─── Parser principal ─────────────────────────────────────────────────────────

export function parseCSV(content: string): ParseResult {
  // Remove BOM
  const clean = content.replace(/^﻿/, '').replace(/^﻿/, '');

  // Detecta separador
  const firstLine = clean.split('\n')[0] ?? '';
  const sep = firstLine.split(';').length > firstLine.split(',').length ? ';' : ',';

  // Divide em linhas e remove vazias
  const lines = clean
    .split('\n')
    .map((l) => l.replace(/\r$/, ''))
    .filter((l) => l.trim().length > 0);

  if (lines.length < 2) {
    return { transactions: [], errors: ['Arquivo vazio ou sem dados'], totalCredit: 0, totalDebit: 0, headers: [] };
  }

  // ─── Localiza a linha de cabeçalho das transações ─────────────────────────
  // Busca nas primeiras 15 linhas a que contém coluna de data
  let headerIdx = 0;
  let headers: string[] = [];

  for (let i = 0; i < Math.min(15, lines.length); i++) {
    const line = lines[i];
    if (!line) continue;
    const cols = line.split(sep);
    const norms = cols.map(normalize);
    const hasDate = DATE_COLS.some((k) => norms.some((h) => h === k || h.includes(k)));
    if (hasDate) {
      headerIdx = i;
      headers = cols;
      break;
    }
  }

  if (!headers.length) {
    headers = (lines[0] ?? '').split(sep);
    headerIdx = 0;
  }

  // ─── Detecta colunas ────────────────────────────────────────────────────────
  const dateIdx    = detectCol(headers, DATE_COLS);
  const descIdx    = detectCol(headers, DESC_COLS);
  const creditIdx  = detectCol(headers, CREDIT_COLS);
  const debitIdx   = detectCol(headers, DEBIT_COLS);
  const amountIdx  = detectCol(headers, AMOUNT_COLS);
  const balanceIdx = detectCol(headers, BALANCE_COLS);
  const refIdx     = detectCol(headers, REF_COLS);

  const bank = detectBank(headers, sep);

  // ─── Processa linhas de dados ─────────────────────────────────────────────
  const transactions: ParsedTransaction[] = [];
  const errors: string[] = [];
  let totalCredit = 0;
  let totalDebit = 0;

  const dataLines = lines.slice(headerIdx + 1);

  for (let i = 0; i < dataLines.length; i++) {
    const line = dataLines[i];
    if (!line?.trim()) continue;

    const cols = line.split(sep);

    // ── Data ──────────────────────────────────────────────────────
    const rawDate = dateIdx >= 0 ? (cols[dateIdx] ?? '') : '';
    const date = parseDate(rawDate);
    if (!date) {
      // Ignora linhas de rodapé/resumo silenciosamente
      const norm = line.toLowerCase();
      const isFooter = ['saldo', 'total', 'extrato', 'periodo', 'banco',
                        'initial', 'final', 'balance', 'credits', 'debits'].some((w) => norm.includes(w));
      if (!isFooter) {
        errors.push(`Linha ${headerIdx + i + 2}: data inválida "${rawDate}"`);
      }
      continue;
    }

    // ── Descrição ─────────────────────────────────────────────────
    const desc = descIdx >= 0 ? (cols[descIdx]?.trim() || 'Sem descrição') : 'Sem descrição';

    // ── Referência externa ─────────────────────────────────────────
    // Combina ID + valor + descrição: mesmo ref_id pode aparecer em múltiplos
    // lançamentos do mesmo evento (débito + liberação + entrada do MP).
    const rawRef = refIdx >= 0 ? cols[refIdx]?.trim() : undefined;
    const external_ref = rawRef
      ? `${rawRef}|${cols[amountIdx] ?? ''}|${desc}`
      : undefined;

    // ── Valor + direção ───────────────────────────────────────────
    let direction: 'in' | 'out' = 'out';
    let amount_cents = 0;

    if (creditIdx >= 0 && debitIdx >= 0) {
      // Colunas separadas de crédito/débito (ex: Itaú)
      const credit = parseBRCents(cols[creditIdx]);
      const debit  = parseBRCents(cols[debitIdx]);
      if (credit && credit > 0)     { direction = 'in';  amount_cents = credit; }
      else if (debit && debit > 0)  { direction = 'out'; amount_cents = debit; }
      else continue; // linha sem valor
    } else if (amountIdx >= 0) {
      // Coluna única com sinal (ex: Mercado Pago, Inter)
      const val = parseBRCents(cols[amountIdx]);
      if (val === null) {
        errors.push(`Linha ${headerIdx + i + 2}: valor inválido "${cols[amountIdx] ?? ''}"`);
        continue;
      }
      if (val > 0)      { direction = 'in';  amount_cents = val; }
      else if (val < 0) { direction = 'out'; amount_cents = Math.abs(val); }
      else continue; // valor zero → ignora
    } else {
      errors.push(`Linha ${headerIdx + i + 2}: nenhuma coluna de valor reconhecida`);
      continue;
    }

    if (amount_cents === 0) continue;

    // ── Saldo ─────────────────────────────────────────────────────
    let balance_cents: number | undefined;
    if (balanceIdx >= 0) {
      const b = parseBRCents(cols[balanceIdx]);
      if (b !== null) balance_cents = b;
    }

    if (direction === 'in') totalCredit += amount_cents;
    else totalDebit += amount_cents;

    transactions.push({
      date,
      description: desc,
      direction,
      amount_cents,
      balance_cents,
      external_ref,
      raw: line,
    });
  }

  return { transactions, errors, bank, totalCredit, totalDebit, headers };
}
